'use client'

import { useEffect, useState } from 'react'
import { useTranslations } from 'next-intl'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/utilities/ui'

const STORAGE_KEY = 'cookie-consent'

type Status = 'pending' | 'visible' | 'hidden'

export function CookieConsent() {
  const t = useTranslations('cookieConsent')
  const [status, setStatus] = useState<Status>('pending')

  useEffect(() => {
    const stored = window.localStorage.getItem(STORAGE_KEY)
    if (stored === 'accepted' || stored === 'denied') {
      setStatus('hidden')
      return
    }
    const timer = setTimeout(() => setStatus('visible'), 500)
    return () => clearTimeout(timer)
  }, [])

  const handleChoice = (choice: 'accepted' | 'denied') => {
    window.localStorage.setItem(STORAGE_KEY, choice)
    setStatus('hidden')
  }

  if (status === 'hidden') return null

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label={t('message')}
      className={cn(
        'fixed z-50 bottom-4 inset-x-4 mx-auto max-w-sm transition-all duration-300 ease-out',
        'sm:inset-x-auto sm:left-auto sm:right-4 sm:mx-0',
        status === 'visible'
          ? 'translate-y-0 opacity-100'
          : 'translate-y-4 opacity-0 pointer-events-none',
      )}
    >
      <div className="rounded-xl border border-border bg-card text-card-foreground shadow-2xl p-5">
        <div className="mb-3 flex items-center justify-between gap-4">
          <h2 className="text-sm font-semibold text-foreground">{t('title')}</h2>
          <button
            type="button"
            onClick={() => handleChoice('denied')}
            aria-label={t('close')}
            className="text-muted-foreground transition-colors hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <p className="mb-6 text-justify text-sm text-muted-foreground">
          {t('message')}{' '}
          <a
            href="/documents/Privacy-Policy_Strand-Forge.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="underline underline-offset-2 hover:text-foreground"
          >
            {t('privacyPolicy')}
          </a>
        </p>

        <div className="flex gap-2">
          <Button
            size="sm"
            className="group relative flex-1 overflow-hidden rounded-xl bg-gradient-to-r from-strandforge-purple to-strandforge-purple-light font-semibold text-white transition-all duration-300 hover:scale-105 hover:from-strandforge-purple-dark hover:to-strandforge-purple hover:shadow-lg hover:shadow-purple-500/25"
            onClick={() => handleChoice('accepted')}
          >
            <span className="absolute inset-0 bg-white/10 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
            <span className="relative z-10">{t('accept')}</span>
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="flex-1 rounded-xl border-white/20 bg-white/5 font-semibold text-white backdrop-blur-md transition-all duration-300 hover:border-white/40 hover:bg-white/10"
            onClick={() => handleChoice('denied')}
          >
            {t('deny')}
          </Button>
        </div>
      </div>
    </div>
  )
}
